/**
 * workspace – one anchor, and every position in the room derived from it.
 *
 * Placement used to be decided by three systems that each took their own
 * bearing from wherever the camera happened to point at the moment they ran:
 * SceneLayoutSystem for the dock and the models, LayoutEngine for the lesson
 * panel, ProfessionalLayoutSystem for a notional asset zone two to four metres
 * out. A head turn between any two of them left the dock and the panel facing
 * different directions, which is why the room never looked arranged — it wasn't.
 * Nothing was wrong with any one of the three; there were simply three answers
 * to one question.
 *
 * Here there is one answer. An anchor is captured once — a position, a bearing
 * flattened to the horizon, and the viewer's real eye height — and the desk, the
 * model slots and the panel are all derived from it. Recentring recaptures the
 * anchor, and the whole workspace moves as one piece, keeping its shape.
 *
 * The numbers below are ergonomic rather than arbitrary:
 *
 *   - Models sit at arm's length on a surface a little under half a metre below
 *     the eye, which is where a desk is whether you are seated or standing.
 *   - They spread across an arc rather than a line, so every model is the same
 *     distance away and the spread stays inside a comfortable turn of the head
 *     however many there are.
 *   - The panel is sized from the angle it subtends, not from a fixed width. A
 *     2.4m panel two metres away spans 62 degrees — wider than can be read
 *     without scanning across it. Sizing by angle keeps it readable at any
 *     distance.
 *
 * Nothing here loads, scales or normalises an asset. This decides only where the
 * slots are.
 */

import * as THREE from 'three';

import { DESK_DROP, PANEL_DROP, measureEyeHeight } from './ergonomics';

/** Distance from the viewer to the models. Comfortable reach, seated. */
export const DESK_REACH = 0.7;

/**
 * Widest the models may spread, in degrees. Beyond about this the outermost
 * model leaves comfortable view and the class starts turning to find it.
 */
export const MAX_MODEL_SPREAD_DEG = 70;

/** Smallest gap between adjacent model centres, so a full dock is still legible. */
export const MIN_MODEL_SPACING_DEG = 12;

/**
 * Bearing of the lesson panel: to the left, out of the way of the models.
 *
 * This is the DEFAULT and also the nearest the panel ever comes to straight
 * ahead; `panelBearingFor` only ever turns it further left. It used to be -20
 * with a 45 degree panel, which put the panel's right edge at +2.5 degrees —
 * across the middle of the desk, in front of the very models it was meant to
 * stand beside. At -45 with a 40 degree panel the right edge is at -25, which
 * keeps the full MODEL_CLEARANCE_DEG margin from up to three models. From four
 * models on, `panelBearingFor` turns the panel further left.
 */
export const PANEL_BEARING_DEG = -45;

/**
 * Furthest left the panel may be turned, however many models there are.
 *
 * Past about this the panel is beside the viewer rather than in front of them,
 * and reading it means turning the whole body rather than the head. The model
 * spread is capped (MAX_MODEL_SPREAD_DEG), so at the default width the panel
 * never actually needs to go this far; the limit is there for a wider panel
 * passed in through `workspaceFrame`'s options.
 */
export const PANEL_BEARING_LIMIT_DEG = -70;

/**
 * Gap between the panel's right edge and the CENTRE of the leftmost model.
 *
 * The slots are model centres, and a model has width, so the edge needs a margin
 * past the centre. It is the same as MIN_MODEL_SPACING_DEG on purpose: the panel
 * then stands no closer to the models than they stand to each other, which is
 * what reads as "beside the models" rather than "on top of the end one".
 */
export const MODEL_CLEARANCE_DEG = 12;

/** How far away the panel sits. Beyond reach, inside comfortable reading range. */
export const PANEL_DISTANCE = 2.0;

/**
 * Angle the panel subtends horizontally. Readable without scanning.
 *
 * 40 rather than the 45 it used to be: moving the panel off to the left means
 * the eye reaches it with a head turn, and a narrower panel keeps that turn —
 * and the distance the eye travels across a line of text — shorter.
 */
export const PANEL_ANGULAR_WIDTH_DEG = 40;

/** The panel canvas is 2048x1280, and stretching it would be visible. */
export const PANEL_ASPECT = 1280 / 2048;

export interface WorkspaceAnchor {
  /** Where the viewer was standing or sitting when this was captured. */
  position: THREE.Vector3;
  /** Where they were facing, flattened to the horizon. */
  forward: THREE.Vector3;
  /** Their measured eye height above the floor. */
  eyeHeight: number;
  /** Floor level, so everything can be expressed against one datum. */
  groundY: number;
}

export interface ModelSlot {
  position: THREE.Vector3;
  /** Facing the viewer. */
  yaw: number;
}

export interface WorkspaceFrame {
  anchor: WorkspaceAnchor;
  /** Height of the desk surface — where a model's feet go. */
  deskY: number;
  dock: {
    center: THREE.Vector3;
    width: number;
    depth: number;
    yaw: number;
  };
  slots: ModelSlot[];
  panel: {
    position: THREE.Vector3;
    width: number;
    height: number;
    yaw: number;
  };
}

/**
 * Where the panel sits and how wide it is, as angles seen from the anchor.
 *
 * Also the shape of `workspaceFrame`'s options, so the result of
 * `flatPanelLayout` can be passed straight in.
 */
export interface PanelLayout {
  /** Bearing of the panel's centre, in degrees, negative to the left. */
  panelBearingDeg: number;
  /** Angle the panel subtends horizontally, in degrees. */
  panelWidthDeg: number;
}

/** A horizon-flattened forward vector, guaranteed non-degenerate. */
function flattenForward(camera: THREE.Camera): THREE.Vector3 {
  const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(camera.quaternion);
  forward.y = 0;
  // Looking straight up or down leaves nothing to flatten. Fall back to -Z
  // rather than normalising a zero vector into NaN and losing the whole room.
  if (forward.lengthSq() < 1e-8) forward.set(0, 0, -1);
  return forward.normalize();
}

/**
 * Capture where the workspace should be built.
 *
 * Called once when the lesson is ready, again when a headset session starts (the
 * first moment the viewer's real eye height is known), and on an explicit
 * recentre. Deliberately NOT called per frame: a workspace that follows the head
 * is a workspace you can never look away from.
 */
export function captureAnchor(camera: THREE.Camera | null, groundY = 0): WorkspaceAnchor {
  if (!camera) {
    return {
      position: new THREE.Vector3(0, groundY, 0),
      forward: new THREE.Vector3(0, 0, -1),
      eyeHeight: measureEyeHeight(null, groundY),
      groundY,
    };
  }
  const position = camera.getWorldPosition(new THREE.Vector3());
  return {
    position,
    forward: flattenForward(camera),
    eyeHeight: measureEyeHeight(camera, groundY),
    groundY,
  };
}

/**
 * A bearing relative to the anchor's forward, in degrees, negative to the LEFT.
 *
 * Left-negative because that is how the bearings read in the room: the panel is
 * at -45, which says "forty-five degrees to your left" without anyone having to
 * remember which way three.js rotates about +Y. It rotates the opposite way,
 * hence the negation here rather than at every call site.
 *
 * Exported, with its inverse `bearingTowards` below, so that a panel the viewer
 * has moved can be remembered in the same terms the frame places it in
 * (panelPlacement.ts). Two copies of the sign convention would be two chances to
 * get it backwards.
 */
export function bearing(forward: THREE.Vector3, degrees: number): THREE.Vector3 {
  return forward
    .clone()
    .applyAxisAngle(new THREE.Vector3(0, 1, 0), THREE.MathUtils.degToRad(-degrees))
    .normalize();
}

/**
 * The inverse of `bearing`: the bearing of `to` as seen from `from`, relative to
 * `forward`, in degrees, negative to the LEFT, in (-180, 180].
 *
 * Height is ignored — a bearing is a direction on the floor plan. A `to` directly
 * above or below `from` has no direction and reads as 0 (straight ahead).
 */
export function bearingTowards(
  forward: THREE.Vector3,
  from: THREE.Vector3,
  to: THREE.Vector3
): number {
  const dx = to.x - from.x;
  const dz = to.z - from.z;
  // Signed angle from `forward` to the offset about +Y is atan2(cross.y, dot).
  // three.js counts +Y rotation to the left, so the bearing is its negation.
  const crossY = forward.z * dx - forward.x * dz;
  const dot = forward.x * dx + forward.z * dz;
  return -THREE.MathUtils.radToDeg(Math.atan2(crossY, dot));
}

/** Yaw that turns an object at `from` to face `to`. */
export function yawTowards(from: THREE.Vector3, to: THREE.Vector3): number {
  return Math.atan2(to.x - from.x, to.z - from.z);
}

/**
 * How widely `count` models spread across the desk arc, in degrees.
 *
 * Enough to separate them, never more than a comfortable turn of the head: one
 * model (or none) has no spread at all, and each extra model adds
 * MIN_MODEL_SPACING_DEG until the arc reaches MAX_MODEL_SPREAD_DEG, after which
 * they close up rather than marching off to the sides. Pulled out of
 * `workspaceFrame` so the panel can be placed clear of the arc without
 * re-deriving it.
 */
export function modelSpreadDeg(count: number): number {
  const n = Math.max(0, Math.floor(count));
  return n > 1 ? Math.min(MAX_MODEL_SPREAD_DEG, MIN_MODEL_SPACING_DEG * (n - 1)) : 0;
}

/**
 * The panel's bearing for `count` models: PANEL_BEARING_DEG unless the models
 * need more room, then just far enough left to clear them.
 *
 * The leftmost model's centre is at -spread/2. The panel's right edge is at
 * bearing + width/2, and should sit MODEL_CLEARANCE_DEG beyond that centre, so
 * the bearing that exactly clears is -(spread/2 + clearance + width/2). It never
 * comes nearer the middle than the default (a lesson with one model still gets
 * the panel where the viewer expects it) and never goes past
 * PANEL_BEARING_LIMIT_DEG. At the default width that is -45 for up to three
 * models, -50 for four and -67 from seven on, where the spread stops growing.
 *
 * `panelWidthDeg` is the width the panel will actually be drawn at, so a wider
 * panel passed to `workspaceFrame` is cleared by its own right edge, not the
 * default one.
 */
export function panelBearingFor(
  count: number,
  panelWidthDeg: number = PANEL_ANGULAR_WIDTH_DEG
): number {
  const clearing = -(modelSpreadDeg(count) / 2 + MODEL_CLEARANCE_DEG + panelWidthDeg / 2);
  return Math.max(PANEL_BEARING_LIMIT_DEG, Math.min(PANEL_BEARING_DEG, clearing));
}

/**
 * Panel placement for a flat screen, from the camera's aspect and VERTICAL fov
 * (what `PerspectiveCamera.fov` holds).
 *
 * The headset bearing does not work on a monitor. A 16:9 window at 75 degrees
 * vertical shows about 107 degrees across, so a 40 degree panel centred at -45
 * would have its left edge at -65 — eleven degrees past the edge of the screen,
 * with the student's only way of seeing it being to drag the view around. On a
 * flat screen the panel is instead fitted to what is visible:
 *
 *   - Width is 42% of the horizontal fov, between 24 degrees (still readable on
 *     a narrow window) and the headset's 40 (no bigger than in VR).
 *   - It sits as far left as it can while keeping its left edge 2 degrees inside
 *     the screen, and never further left than the headset's -45.
 *
 * That gives about -31.8 / 40 at 16:9 and -24.5 / 38.3 at 4:3. On a window so
 * narrow the panel cannot fit beside the middle, it is centred instead.
 *
 * The bearing ignores the model count on purpose: a monitor has no room to move
 * the panel further left, and the viewer can drag it if it covers something.
 */
export function flatPanelLayout(aspect: number, vfovDeg: number): PanelLayout {
  const halfV = THREE.MathUtils.degToRad(vfovDeg / 2);
  const measured = 2 * THREE.MathUtils.radToDeg(Math.atan(Math.tan(halfV) * aspect));
  // A canvas that has not been sized yet reports aspect 0/0 = NaN. Treat that as
  // "no width to speak of", which lands on a small panel straight ahead, rather
  // than handing NaN to every position in the room.
  const hfov = Number.isFinite(measured) && measured > 0 ? measured : 0;
  const panelWidthDeg = THREE.MathUtils.clamp(0.42 * hfov, 24, PANEL_ANGULAR_WIDTH_DEG);
  const leftward = THREE.MathUtils.clamp(hfov / 2 - panelWidthDeg / 2 - 2, 0, -PANEL_BEARING_DEG);
  // `0 - x` rather than `-x`, so a centred panel reads 0 and not -0.
  return { panelBearingDeg: 0 - leftward, panelWidthDeg };
}

/**
 * Every position in the room, for `assetCount` models.
 *
 * The models are laid on an arc centred on the anchor, so each is the same
 * distance from the viewer and the spread grows only until it reaches the
 * comfortable limit — after that they close up rather than marching off to the
 * sides. One model sits straight ahead.
 *
 * The panel is placed by `panelBearingFor`, clear of that arc, unless `opts`
 * says otherwise: a flat screen passes `flatPanelLayout(...)` so the panel stays
 * inside the window. Either field may be given alone; a width on its own still
 * gets a bearing that clears the models at THAT width.
 */
export function workspaceFrame(
  anchor: WorkspaceAnchor,
  assetCount: number,
  opts: Partial<PanelLayout> = {}
): WorkspaceFrame {
  const deskY = Math.max(anchor.groundY + 0.3, anchor.groundY + anchor.eyeHeight - DESK_DROP);
  const count = Math.max(0, Math.floor(assetCount));

  // Spread: enough to separate the models, never more than comfortable.
  const spread = modelSpreadDeg(count);

  const slots: ModelSlot[] = [];
  for (let i = 0; i < count; i += 1) {
    // -spread/2 .. +spread/2, and exactly 0 for a single model.
    const t = count > 1 ? i / (count - 1) - 0.5 : 0;
    const direction = bearing(anchor.forward, t * spread);
    const position = anchor.position.clone().addScaledVector(direction, DESK_REACH);
    position.y = deskY;
    slots.push({ position, yaw: yawTowards(position, anchor.position) });
  }

  // The dock is the surface those slots stand on: wide enough to hold the arc
  // with a margin, never narrower than a single model needs.
  const halfChord = DESK_REACH * Math.sin(THREE.MathUtils.degToRad(spread / 2));
  const dockCenter = anchor.position.clone().addScaledVector(anchor.forward, DESK_REACH);
  dockCenter.y = deskY;
  const dock = {
    center: dockCenter,
    width: Math.max(0.6, halfChord * 2 + 0.4),
    depth: 0.5,
    yaw: yawTowards(dockCenter, anchor.position),
  };

  // The panel: same anchor, deliberate offset, sized by the angle it subtends.
  const panelWidthDeg = opts.panelWidthDeg ?? PANEL_ANGULAR_WIDTH_DEG;
  const panelBearingDeg = opts.panelBearingDeg ?? panelBearingFor(count, panelWidthDeg);
  const panelDirection = bearing(anchor.forward, panelBearingDeg);
  const panelPosition = anchor.position.clone().addScaledVector(panelDirection, PANEL_DISTANCE);
  panelPosition.y = anchor.groundY + anchor.eyeHeight - PANEL_DROP;
  const panelWidth = 2 * PANEL_DISTANCE * Math.tan(THREE.MathUtils.degToRad(panelWidthDeg / 2));

  return {
    anchor,
    deskY,
    dock,
    slots,
    panel: {
      position: panelPosition,
      width: panelWidth,
      height: panelWidth * PANEL_ASPECT,
      yaw: yawTowards(panelPosition, anchor.position),
    },
  };
}
